import type {Page} from '@PosterWhiteboard/page/page.class';
import type {AnimationObject, SlideType} from '@PosterWhiteboard/classes/item-animation.class';
import {AnimateItemType, AnimationSpeed, AnimationType, hasSequentialDesignAnimationEffect, PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {DeepPartial} from '@/global';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {FIXED_SEQUENTIAL_ANIMATION_DELAY_FACTOR, MAX_ITEM_ANIMATION_DELAY_FACTOR, MAX_ITEMS_WITH_FIXED_DELAY_FACTOR} from '@PosterWhiteboard/page/page.types';

const DELAY_FACTOR_ZERO = 0;
const DELAY_FACTOR_VECTOR = 0.1;
const DELAY_FACTOR_IMAGE = 0.2;

const START_TIME_LEEWAY = 3;
const END_TIME_LEEWAY = 3;
export const MIN_DURATION_BETWEEN_ANIMATIONS = 1;

export class PageAnimation extends CommonMethods {
  public page: Page;
  public type: AnimationType;
  public direction: SlideType | PositionType;
  public speed: AnimationSpeed;

  protected positionSortedItems: ItemType[] = [];
  protected zIndexSortedItems: ItemType[] = [];

  constructor(page: Page) {
    super();
    this.page = page;
    this.type = AnimationType.NONE;
    this.direction = PositionType.RIGHT;
    this.speed = AnimationSpeed.MEDIUM;
  }

  public toObject(): AnimationObject {
    return {
      type: this.type,
      speed: this.speed,
      direction: this.direction,
    };
  }

  public updatePageItemsAnimation(oldAnimation: AnimationObject | null): void {}

  public replayPoster(): void {}

  public hasAnimation(): boolean {
    return this.type !== AnimationType.NONE;
  }

  public updateFromObject(
    animation: DeepPartial<AnimationObject>,
    {undoable = true, fireModifiedEvent = true, checkForDurationUpdate = false, replayPosterOnUpdateDone = false}: UpdateFromObjectOpts = {}
  ): void {
    let checkForTransparentBackground = false;
    const oldAnimation = this.toObject();
    if (!this.hasAnimation() && animation?.type !== AnimationType.NONE) {
      checkForTransparentBackground = true;
    }
    this.copyVals(animation);
    this.updatePageItemsAnimation(replayPosterOnUpdateDone ? oldAnimation : null);

    if (checkForDurationUpdate) {
      this.page.calculateAndUpdatePageDuration();
    }

    if (checkForTransparentBackground && this.page.background.isTransparentBackground()) {
      this.page.background.updateBackgroundToBeNonTransparent();
    }

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.fire('modified');
    }

    this.page.poster.redux.updateReduxData();

    if (replayPosterOnUpdateDone) {
      this.replayPoster();
    }
  }

  public getMaxItemAnimationDelay(): number {
    const duration = this.page.getDuration();

    if (hasSequentialDesignAnimationEffect(this.type)) {
      return duration * FIXED_SEQUENTIAL_ANIMATION_DELAY_FACTOR * MAX_ITEMS_WITH_FIXED_DELAY_FACTOR;
    }

    return MAX_ITEM_ANIMATION_DELAY_FACTOR * duration;
  }

  protected itemHasDifferentStartTime(item: ItemType): boolean {
    return item.isTranscript() || (item.isSlideshow() && item.onPageStartTime > START_TIME_LEEWAY);
  }

  protected itemHasDifferentEndTime(item: ItemType, pageDuration: number): boolean {
    return item.isTranscript() || (item.isSlideshow() && item.getDuration() < pageDuration - END_TIME_LEEWAY);
  }

  protected getSortedGraphicItemsAccordingToPosition = (type: AnimateItemType, pageDuration: number): ItemType[] => {
    if (this.positionSortedItems.length) {
      return this.positionSortedItems;
    }
    const items = this.page.items.getItems();
    const filteredItems = items.filter((item) => {
      const isAnimationApplicable = [AnimationType.RISE, AnimationType.BLOCK, AnimationType.PAN, AnimationType.TUMBLE, AnimationType.PEEK, AnimationType.WIPE].includes(
        type === AnimateItemType.INTRO_ANIMATION ? item.introAnimation.type : item.outroAnimation.type
      );
      return isAnimationApplicable && (type === AnimateItemType.INTRO_ANIMATION ? !this.itemHasDifferentStartTime(item) : !this.itemHasDifferentEndTime(item, pageDuration));
    });
    this.positionSortedItems = filteredItems.sort((a: ItemType, b: ItemType) => {
      const boundTopA = a.fabricObject.getBoundingRect().top;
      const boundLeftA = a.fabricObject.getBoundingRect().left;
      const boundTopB = b.fabricObject.getBoundingRect().top;
      const boundLeftB = b.fabricObject.getBoundingRect().left;

      return boundTopA !== boundTopB ? boundTopA - boundTopB : boundLeftA - boundLeftB;
    });
    return this.positionSortedItems;
  };

  protected getSortedGraphicItemsAccordingToZIndex = (type: AnimateItemType, pageDuration: number): ItemType[] => {
    if (this.zIndexSortedItems.length) {
      return this.zIndexSortedItems;
    }
    const items = this.page.items.getItems();
    const sortedItems = items.filter((item) => {
      const isAnimationApplicable = [AnimationType.POP, AnimationType.JELLO, AnimationType.ROTATE].includes(
        type === AnimateItemType.INTRO_ANIMATION ? item.introAnimation.type : item.outroAnimation.type
      );
      return isAnimationApplicable && (type === AnimateItemType.INTRO_ANIMATION ? !this.itemHasDifferentStartTime(item) : !this.itemHasDifferentEndTime(item, pageDuration));
    });
    const fabricObjectsInCanvas = this.page.fabricCanvas.getObjects();

    this.zIndexSortedItems = sortedItems.sort((a, b) => {
      return fabricObjectsInCanvas.indexOf(a.fabricObject) - fabricObjectsInCanvas.indexOf(b.fabricObject);
    });

    return this.zIndexSortedItems;
  };

  protected getDelayForModelType = (itemType: ITEM_TYPE, speed: number): number => {
    switch (itemType) {
      case ITEM_TYPE.VECTOR:
        return speed * DELAY_FACTOR_VECTOR;
      case ITEM_TYPE.IMAGE:
      case ITEM_TYPE.DRAWING:
        return speed * DELAY_FACTOR_IMAGE;
      case ITEM_TYPE.TEXT:
      case ITEM_TYPE.FANCY_TEXT:
      case ITEM_TYPE.TAB:
      case ITEM_TYPE.TABLE:
      case ITEM_TYPE.MENU:
      case ITEM_TYPE.SLIDESHOW:
        return speed * MAX_ITEM_ANIMATION_DELAY_FACTOR;
      default:
        return DELAY_FACTOR_ZERO;
    }
  };

  protected getDelayFactorByTotalObjects = (speed: number): number => {
    if (this.page) {
      const totalGraphicItems = Object.keys(this.page.items.itemsHashMap).length;
      const totalObjectsWithDelay = totalGraphicItems - 1;

      return totalObjectsWithDelay > 0 ? (MAX_ITEM_ANIMATION_DELAY_FACTOR * speed) / totalObjectsWithDelay : 0;
    }
    return 0;
  };

  protected getAlternativeDelayFactor = (totalGraphicItems = 0, speed: number): number => {
    if (speed) {
      if (totalGraphicItems <= MAX_ITEMS_WITH_FIXED_DELAY_FACTOR + 1) {
        return speed * FIXED_SEQUENTIAL_ANIMATION_DELAY_FACTOR;
      }
      return (speed * FIXED_SEQUENTIAL_ANIMATION_DELAY_FACTOR * MAX_ITEMS_WITH_FIXED_DELAY_FACTOR) / (totalGraphicItems - 1);
    }
    return 0;
  };

  protected getTumbleAnimationDirection = (graphicItem: ItemType): PositionType => {
    const itemZIndex = this.page.fabricCanvas.getObjects().indexOf(graphicItem.fabricObject);
    switch (itemZIndex % 8) {
      case 0:
        return PositionType.TOP_LEFT;
      case 1:
        return PositionType.BOTTOM;
      case 2:
        return PositionType.RIGHT;
      case 3:
        return PositionType.BOTTOM_RIGHT;
      case 4:
        return PositionType.TOP;
      case 5:
        return PositionType.LEFT;
      case 6:
        return PositionType.TOP_RIGHT;
      default:
        return PositionType.BOTTOM_LEFT;
    }
  };

  protected isAnimationDurationApplicableOnItem = (item: ItemType): boolean => {
    return !item.isSlideshowSlide() && !item.isTranscript() && !item.isImageBackground();
  };
}
